import { Component, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { format, formatISO, parseISO } from 'date-fns';
import flatten from 'lodash/flatten';
import { combineLatest, forkJoin, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, skip, startWith, switchMap, tap } from 'rxjs/operators';

import { FieldType } from '../../enums/field-type.enum';
import { FwRulesFieldsEnum } from '../../enums/fw-rules-fields.enum';
import { splitFilterString } from '../../filter.utils';
import { CoreApiService } from '../../../core-api.service';
import { Suggestion } from '../fw-chip-input/fw-chip-input.component';
import { buildRuleField, consolidateCommonRules, convertRuleToStringArray, convertStringToRule, emptyRule, getOperatorOptions, getValueInterface } from './fw-rules-creator.utils';
import { Option } from '../../models/option.model';
import { SearchField } from '../../models/search/search-field.model';
import { replayValue } from '../../observable.utils';
import { buildRuleString } from '../../../search/advanced-query/visual-query.utils';

@Component({
  host: { 'class': 'fw-rules-creator' },
  selector: 'fw-rules-creator',
  styleUrls: ['./fw-rules-creator.component.scss'],
  templateUrl: './fw-rules-creator.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FwRulesCreatorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FwRulesCreatorComponent),
      multi: true,
    },
  ],
})
export class FwRulesCreatorComponent implements ControlValueAccessor, OnDestroy {
  @Input() card = true
  @Input('operatorOptions') operatorOptionsOverride?: Option[]
  @Input() searchFieldFilter?: (fields: SearchField[]) => SearchField[]
  @Input() searchFieldSource?: (value: string) => Observable<SearchField[]>
  @Input() enableRegex?: boolean = true
  @Input() tooltip?: string

  @ViewChild('autoValue', { static: true }) autoValue: MatAutocomplete

  fieldCache = new Map<string, SearchField>()
  fieldSuggestions$: Observable<SearchField[]> = of([])
  onChange: Function = () => {} // eslint-disable-line @typescript-eslint/no-empty-function
  onTouched: Function = () => {} // eslint-disable-line @typescript-eslint/no-empty-function
  readable = true
  rules: FormArray = this.formBuilder.array([])
  valueSuggestions$: Observable<Suggestion[]> = of([])

  suggestionInput$ = new Subject<string>()
  private subscriptions = {
    rulesValueChange: Subscription.EMPTY,
    fieldValueChange: Subscription.EMPTY,
    autocomplete: Subscription.EMPTY,
    consolidatedRules: Subscription.EMPTY,
  }

  constructor(
    private coreApi: CoreApiService,
    private formBuilder: FormBuilder,
  ) {
    this.subscriptions.rulesValueChange = this.rules.valueChanges.pipe(
      filter(() => this.readable),
    ).subscribe(values => {
      this.onChange(flatten(values.map((value) => convertRuleToStringArray(this.fieldCache, value))));
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isValid(): boolean {
    return this.rules.valid;
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  validate(control: AbstractControl): ValidationErrors | null {
    return this.rules.valid ? null : { required: true };
  }

  requiredIfField(control: AbstractControl): ValidationErrors | null {
    const hasValue = Boolean(typeof control?.value === 'number' || control?.value?.length);
    const isEmpty = Boolean(!control.parent?.get(FwRulesFieldsEnum.Field).value && !hasValue);
    if (!isEmpty && !hasValue) {
      return { required: true };
    }
    return null;
  }

  requiredIfValue(control: AbstractControl): ValidationErrors | null {
    const valueControl = control.parent?.get(
      Object.keys(control.parent?.value || {})
        .filter((key) =>
          ![FwRulesFieldsEnum.Field, FwRulesFieldsEnum.Operator].includes(key as FwRulesFieldsEnum)
        )[0]
    );
    if (!control.value?.length && Boolean(typeof control?.value === 'number' || control?.value?.length)) {
      return { required: true };
    }
    return null;
  }

  getRuleFormGroup(rule = emptyRule): FormGroup {
    const timestampDate = rule.date.timestampDate ? parseISO(`${rule.date.timestampDate}`) : parseISO(formatISO(new Date()));
    const timestampTime = format(timestampDate, 'HH:mm');
    const form = this.formBuilder.group({
      [FwRulesFieldsEnum.Field]: this.formBuilder.control(rule.field.name, [this.requiredIfValue]),
      [FwRulesFieldsEnum.Operator]: this.formBuilder.control(rule.operator),
      [FwRulesFieldsEnum.Text]: this.formBuilder.control(rule.text || '', [this.requiredIfField]),
      [FwRulesFieldsEnum.Chips]: this.formBuilder.control(rule.chips || [], [this.requiredIfField]),
      [FwRulesFieldsEnum.Number]: this.formBuilder.control(typeof rule.number === 'number' ? rule.number : null, [this.requiredIfField]),
      [FwRulesFieldsEnum.Boolean]: this.formBuilder.control(rule.boolean !== false), // default true
      [FwRulesFieldsEnum.Date]: this.formBuilder.group({
        timestampDate: this.formBuilder.control(timestampDate),
        timestampTime: this.formBuilder.control(timestampTime),
      }),
      [FwRulesFieldsEnum.Regex]: this.formBuilder.control({ value: rule.regex, disabled: !this.enableRegex }),
    });
    const field = form.get(FwRulesFieldsEnum.Field);
    this.subscriptions.fieldValueChange.unsubscribe();
    this.subscriptions.fieldValueChange = field.valueChanges.pipe(
      debounceTime(200),
      startWith(field.value),
    ).subscribe(value => {
      const operator = form.get(FwRulesFieldsEnum.Operator);
      const operatorOptions = this.getOperatorOptions(form);
      operator.setValue(operatorOptions.find((opt) => opt.value === operator.value) ? operator.value : operatorOptions[0]?.value);
      const valueInterface = this.getValueInterface(form);
      // must disable irrelevant controls for validation to work correctly
      for (const [key, control] of Object.entries(form.controls)) {
        control[[valueInterface, 'field', 'operator'].includes(key) ? 'enable' : 'disable']();
      }
      // regex controls should be re-enabled if it is appropriate to do so AFTER disabling irrelevant controls
      const regex = form.get(FwRulesFieldsEnum.Regex);
      const field = this.fieldCache.get(value) || { type: 'string' };
      regex[field.type === 'string' && this.enableRegex ? 'enable' : 'disable']();
    });
    this.subscriptions.autocomplete.unsubscribe();
    this.subscriptions.autocomplete = combineLatest([
      replayValue(form.get(FwRulesFieldsEnum.Text)),
      replayValue(form.get(FwRulesFieldsEnum.Chips)),
    ]).pipe(skip(1),
    ).subscribe(() => { // skip first emission to preserve pristine state with initial value
      field.updateValueAndValidity();
      field.markAsTouched();
    });
    this.bindFieldAutocomplete(form);
    this.bindSuggestionAutocomplete(form);
    return form;
  }

  /**
   * Fetch field name suggestions for this rule and feed them into the autocomplete.
   */
  bindFieldAutocomplete(rule: FormGroup): void {
    this.fieldSuggestions$ = rule.controls.field.valueChanges.pipe(
      debounceTime(200),
      startWith(rule.controls.field.value),
      filter(value => Boolean(value)),
      switchMap(this.searchFieldSource || (value => this.coreApi.dataexplorer.fields({ field: value }))),
      map(this.searchFieldFilter || (v => v)),
      tap(fields => {
        fields.forEach(field => this.fieldCache.set(field.name, field));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Fetch value suggestions for this rule and feed them into the autocomplete.
   */
  bindSuggestionAutocomplete(rule: FormGroup): void {
    this.valueSuggestions$ = merge(
      rule.controls.text.valueChanges,
      this.suggestionInput$,
    ).pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(value => {
        const json = rule.getRawValue();
        const visualRule = {
          ...json,
          operator: 'is', // normalize value format
          field: buildRuleField(this.fieldCache, json.field),
        };
        return this.coreApi.dataexplorer.suggest({
          structured_query: buildRuleString({ ...visualRule, chips: [value] }),
        });
      }),
      map(({ suggestions }) => suggestions.map(v => {
        const value = v.value.replace(/"/g, '');
        return { ...v, value, tooltip: value };
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getOperatorOptions(rule: FormGroup): Option[] {
    return this.operatorOptionsOverride?.length > 0 ? this.operatorOptionsOverride : getOperatorOptions(this.fieldCache, rule);
  }

  getValueInterface(rule: FormGroup): string {
    return getValueInterface(this.fieldCache, rule);
  }

  addRule(index: number): void {
    this.rules.insert(index + 1, this.getRuleFormGroup());
  }

  removeRule(ruleIndex: number): void {
    if (this.rules.length > 1) {
      this.rules.removeAt(ruleIndex);
    } else {
      this.rules.setControl(0, this.getRuleFormGroup()); // reset
    }
  }

  addToRulesFormGroup(rules: FormGroup[]): void {
    this.rules.clear();
    for (const form of rules) {
      this.rules.push(form);
    }
    this.readable = true;
  }

  /** ControlValueAccessor methods below */
  writeValue(inputs: string[]): void {
    this.readable = false;
    const formGroups = [];
    const rules = [];
    if (inputs.length > 0) {
      this.subscriptions.consolidatedRules.unsubscribe();
      this.subscriptions.consolidatedRules = forkJoin(
        inputs.map((input, i) => {
          const inputName = splitFilterString(input)[0];
          return (
            this.searchFieldSource
              ? this.searchFieldSource(inputName)
              : this.coreApi.dataexplorer.fields({ field: inputName })
          ).pipe(
            tap((fieldSource) => {
              const fields = !fieldSource.length ? [{ name: inputName, type: FieldType.Chips }] : fieldSource;
              this.fieldCache.set(fields[0].name, fields[0]);
              const rule = convertStringToRule(this.fieldCache, input);
              if (rule) {
                rules[i] = rule;
              }
            }),
          );
        }),
      ).subscribe(() => {
        if (rules.length === 0) {
          rules.push(emptyRule);
        }
        const consolidatedRules = consolidateCommonRules(rules);
        for (const rule of consolidatedRules) {
          formGroups.push(this.getRuleFormGroup(rule));
        }
        this.addToRulesFormGroup(formGroups);
      });
    } else {
      formGroups.push(this.getRuleFormGroup());
      this.addToRulesFormGroup(formGroups);
    }
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.rules[isDisabled ? 'disable' : 'enable']();
  }
  /**/
}
